import React, { useState } from 'react';
import { Checkbox, DefaultButton, Dialog, DialogFooter, DialogType, MessageBar, MessageBarType, PrimaryButton, ProgressIndicator } from '@fluentui/react';
import { importCompetitionCsv } from '../api/prospectingApi';

export default function CompetitionImportDialog({ token, open, onDismiss, onChanged }) {
  const [file, setFile] = useState(null);
  const [push, setPush] = useState(false);
  const [recoverMissing, setRecoverMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const failedRows = (result?.rows || []).filter((row) => row.status === 'error');
  const errorCounts = failedRows.reduce((out, row) => {
    const message = String(row.message || 'Nieznany błąd');
    // Aggregate repetitive missing-registry messages while preserving full
    // details in the downloadable per-row CSV report.
    const group = message.startsWith('Nie znaleziono numeru rejestrowego') ? 'Nie znaleziono numeru rejestrowego w ONE'
      : message.startsWith('Podmiot jest oznaczony jako nasz klient') ? 'Placówka jest już naszym klientem'
      : message.startsWith('Niejednoznaczny numer rejestrowy') ? 'Niejednoznaczny numer rejestrowy'
      : message;
    out[group] = (out[group] || 0) + 1;
    return out;
  }, {});

  function downloadErrors() {
    if (!failedRows.length) return;
    const escapeCsv = (value) => {
      let str = String(value ?? '');
      if (/^[=+@-]/.test(str)) str = "'" + str; // Spreadsheet formula injection safeguard.
      return '"' + str.replace(/"/g, '""') + '"';
    };
    const lines = [['line', 'registry_number', 'message'], ...failedRows.map((row) => [row.line, row.registry_number, row.message])];
    const csv = '\uFEFF' + lines.map((row) => row.map(escapeCsv).join(';')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = 'competition_import_errors.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally { URL.revokeObjectURL(url); }
  }

  async function upload() {
    if (!file || busy) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const response = await importCompetitionCsv(token, file, { pushToPipedrive: push, recoverMissing });
      setResult(response);
      if (onChanged) onChanged();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  return (
    <Dialog hidden={!open} onDismiss={busy ? undefined : onDismiss} dialogContentProps={{ type: DialogType.largeHeader, title: 'Import konkurencji', subText: 'UTF-8 CSV. Identyfikacja odbywa się po Numerze Rejestrowym, nie po nazwie placówki.' }} minWidth={680}>
      <div className="prospecting-csv-example">
        <strong>Format:</strong>
        <code>registry_number;product;competitor;confidence;verified_at;source_url;note</code>
        <small>Domyślnie: szybka aktualizacja z lokalnej bazy ONE, bez pobierania RSPO/Empatii i bez synchronizacji Pipedrive. Przykłady numerów: 80961 · E10016/Z · T3021163. Product: kidplace, grants, erecruitment.
          Opcjonalnie: opening_date, opening_evidence_url, opening_status=confirmed (potwierdzona data otwarcia).</small>
      </div>
      <input type="file" accept=".csv,text/csv" onChange={(event) => setFile(event.target.files?.[0] || null)} />
      <Checkbox label="Dodatkowo pobierz brakujące placówki z RSPO/Empatii (wolniej, maks. 12 na import)" checked={recoverMissing} onChange={(_, value) => setRecoverMissing(Boolean(value))} />
      <Checkbox label="Jeżeli podmiot jest już w CRM i nie ma etykiety konkurencji — dodaj ją również w Pipedrive" checked={push} onChange={(_, value) => setPush(Boolean(value))} />
      {busy ? <ProgressIndicator label="Importuję konkurencję…" /> : null}
      {error ? <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar> : null}
      {result ? <MessageBar messageBarType={result.failed ? MessageBarType.warning : MessageBarType.success}>Zaimportowano: {result.imported || 0}. Nowe placówki z rejestrów: {result.recovered || 0}. Błędy: {result.failed || 0}. Czas: {Number.isFinite(result.elapsed_ms) ? `${(result.elapsed_ms / 1000).toFixed(1)} s` : "—"}.</MessageBar> : null}
      {failedRows.length > 0 ? (
        <div style={{ marginTop: 12 }}>
          <strong>Dlaczego część wierszy nie została zaimportowana?</strong>
          <ul style={{ marginTop: 6 }}>
            {Object.entries(errorCounts).map(([message, count]) => <li key={message}>{count} × {message}</li>)}
          </ul>
          <DefaultButton text={`Pobierz błędy CSV (${failedRows.length})`} iconProps={{ iconName: 'Download' }} onClick={downloadErrors} />
          <div style={{ marginTop: 8, maxHeight: 155, overflowY: 'auto', fontSize: 12 }}>
            {failedRows.slice(0, 15).map((row, i) => <div key={`${row.line}-${i}`}>Wiersz {row.line}, {row.registry_number}: {row.message}</div>)}
            {failedRows.length > 15 ? <div>Więcej szczegółów w pobranym CSV.</div> : null}
          </div>
        </div>
      ) : null}
      <DialogFooter>
        <PrimaryButton text="Importuj" disabled={!file || busy} onClick={upload} />
        <DefaultButton text="Zamknij" disabled={busy} onClick={onDismiss} />
      </DialogFooter>
    </Dialog>
  );
}
